import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../utils/api';
import { toast } from '../../utils/notification';
import { UserCircleIcon, CheckCircleIcon, CameraIcon } from '@heroicons/react/24/outline';

const JURISDICTION_OPTIONS = [
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'BW', label: 'Botswana' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'NA', label: 'Namibia' },
  { value: 'MZ', label: 'Mozambique' },
];

const UserProfile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    jurisdiction: 'ZW',
    job_title: '',
    department: '',
    bio: '',
    address: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        jurisdiction: user.jurisdiction || 'ZW',
        job_title: user.job_title || '',
        department: user.department || '',
        bio: user.profile?.bio || '',
        address: user.profile?.address || '',
      });
      setAvatarPreview(user.profile?.profile_picture_url || null);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5 MB');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    try {
      let response;
      if (avatarFile) {
        const form = new FormData();
        Object.entries(profileData).forEach(([k, v]) => {
          if (k !== 'email' && v !== null && v !== undefined) form.append(k, v);
        });
        form.append('profile_picture', avatarFile);
        response = await apiRequest.patch('/api/accounts/me/', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const { email, ...payload } = profileData;
        response = await apiRequest.patch('/api/accounts/me/', payload);
      }

      updateUser(response.data);
      setAvatarFile(null);
      setSuccess(true);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const initials = `${(profileData.first_name?.[0] || '').toUpperCase()}${(profileData.last_name?.[0] || '').toUpperCase()}`;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Profile Information</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Personal details, contact, and how you appear to colleagues.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-4 py-5 sm:p-6">
            {success && (
              <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" aria-hidden="true" />
                  <p className="ml-3 text-sm font-medium text-green-800 dark:text-green-200">Profile updated successfully!</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              {/* Avatar */}
              <div className="sm:col-span-6">
                <div className="flex items-center gap-4">
                  <div className="h-24 w-24 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Profile" className="h-full w-full object-cover" />
                    ) : initials ? (
                      <span className="text-2xl font-semibold text-primary-700 dark:text-primary-300">{initials}</span>
                    ) : (
                      <UserCircleIcon className="h-20 w-20 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <CameraIcon className="h-4 w-4" />
                      {avatarPreview ? 'Change photo' : 'Upload photo'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">JPG or PNG, up to 5 MB.</p>
                  </div>
                </div>
              </div>

              {/* First name */}
              <div className="sm:col-span-3">
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">First name</label>
                <input
                  type="text" name="first_name" id="first_name"
                  value={profileData.first_name} onChange={handleChange}
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Last name */}
              <div className="sm:col-span-3">
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last name</label>
                <input
                  type="text" name="last_name" id="last_name"
                  value={profileData.last_name} onChange={handleChange}
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Email */}
              <div className="sm:col-span-3">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
                <input
                  id="email" name="email" type="email"
                  value={profileData.email} disabled
                  className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 bg-gray-50 cursor-not-allowed text-gray-500 dark:text-gray-400"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Contact admin to change your email address</p>
              </div>

              {/* Phone */}
              <div className="sm:col-span-3">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone number</label>
                <input
                  type="tel" name="phone" id="phone"
                  value={profileData.phone} onChange={handleChange} autoComplete="tel"
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Job title */}
              <div className="sm:col-span-3">
                <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Job title</label>
                <input
                  type="text" name="job_title" id="job_title"
                  value={profileData.job_title} onChange={handleChange}
                  placeholder="e.g. Senior Associate"
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Department */}
              <div className="sm:col-span-3">
                <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                <input
                  type="text" name="department" id="department"
                  value={profileData.department} onChange={handleChange}
                  placeholder="e.g. Litigation, Conveyancing"
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Jurisdiction */}
              <div className="sm:col-span-3">
                <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Jurisdiction</label>
                <select
                  name="jurisdiction" id="jurisdiction"
                  value={profileData.jurisdiction} onChange={handleChange}
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  {JURISDICTION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Address */}
              <div className="sm:col-span-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                <textarea
                  id="address" name="address" rows={2}
                  value={profileData.address} onChange={handleChange}
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Bio */}
              <div className="sm:col-span-6">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bio / About</label>
                <textarea
                  id="bio" name="bio" rows={4}
                  value={profileData.bio} onChange={handleChange}
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Brief description shown to colleagues and clients.</p>
              </div>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 text-right sm:px-6">
            <button
              type="submit"
              disabled={loading}
              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
